import { useState, useEffect } from 'react';
import { Clock, Calendar, Plus, Trash2, Save } from 'lucide-react';
import { API_ENDPOINTS, isUsingFallback } from '../utils/api';
import { dbBusinessHours, type BusinessHoursData } from '../utils/dbBackend';

interface TimeSlot {
  start: string;
  end: string;
}

interface DaySettings {
  enabled: boolean;
  hours: TimeSlot[];
}

interface BusinessHoursSettings {
  workingDays: {
    sunday: DaySettings;
    monday: DaySettings;
    tuesday: DaySettings;
    wednesday: DaySettings;
    thursday: DaySettings;
    friday: DaySettings;
    saturday: DaySettings;
  };
  holidays: string[]; // Array of dates in YYYY-MM-DD format
  slotDuration: number; // minutes
}

interface BusinessHoursSettingsProps {
  accessToken: string;
}

const dayNames = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

export function BusinessHoursSettings({ accessToken }: BusinessHoursSettingsProps) {
  const [settings, setSettings] = useState<BusinessHoursSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newHoliday, setNewHoliday] = useState('');
  const useLocal = isUsingFallback();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      if (useLocal) {
        // Use IndexedDB (Dexie)
        const dbData = await dbBusinessHours.get();
        // Convert to component's expected format
        const converted: BusinessHoursSettings = {
          workingDays: {
            sunday: { enabled: dbData.sunday.isOpen, hours: [{ start: dbData.sunday.openTime, end: dbData.sunday.closeTime }] },
            monday: { enabled: dbData.monday.isOpen, hours: [{ start: dbData.monday.openTime, end: dbData.monday.closeTime }] },
            tuesday: { enabled: dbData.tuesday.isOpen, hours: [{ start: dbData.tuesday.openTime, end: dbData.tuesday.closeTime }] },
            wednesday: { enabled: dbData.wednesday.isOpen, hours: [{ start: dbData.wednesday.openTime, end: dbData.wednesday.closeTime }] },
            thursday: { enabled: dbData.thursday.isOpen, hours: [{ start: dbData.thursday.openTime, end: dbData.thursday.closeTime }] },
            friday: { enabled: dbData.friday.isOpen, hours: [{ start: dbData.friday.openTime, end: dbData.friday.closeTime }] },
            saturday: { enabled: dbData.saturday.isOpen, hours: [{ start: dbData.saturday.openTime, end: dbData.saturday.closeTime }] },
          },
          holidays: dbData.holidays || [],
          slotDuration: dbData.slotDuration || 60,
        };
        setSettings(converted);
      } else {
        const response = await fetch(API_ENDPOINTS.businessHours);
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      if (useLocal) {
        // Convert back to DB format and save to IndexedDB
        const dbFormat: BusinessHoursData = {
          sunday: { isOpen: settings.workingDays.sunday.enabled, openTime: settings.workingDays.sunday.hours[0]?.start || '09:00', closeTime: settings.workingDays.sunday.hours[0]?.end || '18:00' },
          monday: { isOpen: settings.workingDays.monday.enabled, openTime: settings.workingDays.monday.hours[0]?.start || '09:00', closeTime: settings.workingDays.monday.hours[0]?.end || '18:00' },
          tuesday: { isOpen: settings.workingDays.tuesday.enabled, openTime: settings.workingDays.tuesday.hours[0]?.start || '09:00', closeTime: settings.workingDays.tuesday.hours[0]?.end || '18:00' },
          wednesday: { isOpen: settings.workingDays.wednesday.enabled, openTime: settings.workingDays.wednesday.hours[0]?.start || '09:00', closeTime: settings.workingDays.wednesday.hours[0]?.end || '18:00' },
          thursday: { isOpen: settings.workingDays.thursday.enabled, openTime: settings.workingDays.thursday.hours[0]?.start || '09:00', closeTime: settings.workingDays.thursday.hours[0]?.end || '18:00' },
          friday: { isOpen: settings.workingDays.friday.enabled, openTime: settings.workingDays.friday.hours[0]?.start || '09:00', closeTime: settings.workingDays.friday.hours[0]?.end || '16:00' },
          saturday: { isOpen: settings.workingDays.saturday.enabled, openTime: settings.workingDays.saturday.hours[0]?.start || '09:00', closeTime: settings.workingDays.saturday.hours[0]?.end || '18:00' },
          holidays: settings.holidays,
          slotDuration: settings.slotDuration,
        };
        await dbBusinessHours.update(dbFormat);
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        const response = await fetch(
          API_ENDPOINTS.businessHours,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(settings),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to save settings');
        }

        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: keyof BusinessHoursSettings['workingDays']) => {
    if (!settings) return;
    setSettings({
      ...settings,
      workingDays: {
        ...settings.workingDays,
        [day]: {
          ...settings.workingDays[day],
          enabled: !settings.workingDays[day].enabled,
        },
      },
    });
  };

  const updateDayHours = (
    day: keyof BusinessHoursSettings['workingDays'],
    index: number,
    field: 'start' | 'end',
    value: string
  ) => {
    if (!settings) return;
    const newHours = [...settings.workingDays[day].hours];
    newHours[index] = { ...newHours[index], [field]: value };
    setSettings({
      ...settings,
      workingDays: {
        ...settings.workingDays,
        [day]: {
          ...settings.workingDays[day],
          hours: newHours,
        },
      },
    });
  };

  const addTimeSlot = (day: keyof BusinessHoursSettings['workingDays']) => {
    if (!settings) return;
    const newHours = [...settings.workingDays[day].hours, { start: '09:00', end: '17:00' }];
    setSettings({
      ...settings,
      workingDays: {
        ...settings.workingDays,
        [day]: {
          ...settings.workingDays[day],
          hours: newHours,
        },
      },
    });
  };

  const removeTimeSlot = (day: keyof BusinessHoursSettings['workingDays'], index: number) => {
    if (!settings) return;
    const newHours = settings.workingDays[day].hours.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      workingDays: {
        ...settings.workingDays,
        [day]: {
          ...settings.workingDays[day],
          hours: newHours,
        },
      },
    });
  };

  const addHoliday = () => {
    if (!settings || !newHoliday) return;
    setSettings({
      ...settings,
      holidays: [...settings.holidays, newHoliday].sort(),
    });
    setNewHoliday('');
  };

  const removeHoliday = (holiday: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      holidays: settings.holidays.filter(h => h !== holiday),
    });
  };

  if (loading) {
    return <div className="p-6 text-center">Loading settings...</div>;
  }

  if (!settings) {
    return <div className="p-6 text-center text-red-600">Failed to load settings</div>;
  }

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
          }}
        >
          {message.text}
        </div>
      )}

      {/* Working Days */}
      <div
        className="p-6 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
      >
        <h3 className="mb-4 flex items-center gap-2" style={{ color: '#444444' }}>
          <Clock size={20} style={{ color: '#F29B70' }} />
          Working Days & Hours
        </h3>

        <div className="space-y-4">
          {(Object.keys(settings.workingDays) as Array<keyof BusinessHoursSettings['workingDays']>).map((day) => (
            <div key={day} className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(242, 155, 112, 0.1)' }}>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.workingDays[day].enabled}
                    onChange={() => toggleDay(day)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: '#F29B70' }}
                  />
                  <span style={{ color: '#444444' }}>{dayNames[day]}</span>
                </label>

                {settings.workingDays[day].enabled && (
                  <button
                    onClick={() => addTimeSlot(day)}
                    className="px-3 py-1 rounded-lg hover:opacity-80 transition-all flex items-center gap-1"
                    style={{ backgroundColor: '#F29B70', color: '#444444' }}
                  >
                    <Plus size={16} />
                    Add Hours
                  </button>
                )}
              </div>

              {settings.workingDays[day].enabled && (
                <div className="space-y-2 ml-7">
                  {settings.workingDays[day].hours.map((slot, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={slot.start}
                        onChange={(e) => updateDayHours(day, index, 'start', e.target.value)}
                        className="px-3 py-2 rounded-lg border"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          borderColor: '#E0D5D1',
                          color: '#444444',
                        }}
                      />
                      <span style={{ color: '#444444' }}>to</span>
                      <input
                        type="time"
                        value={slot.end}
                        onChange={(e) => updateDayHours(day, index, 'end', e.target.value)}
                        className="px-3 py-2 rounded-lg border"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          borderColor: '#E0D5D1',
                          color: '#444444',
                        }}
                      />
                      {settings.workingDays[day].hours.length > 1 && (
                        <button
                          onClick={() => removeTimeSlot(day, index)}
                          className="p-2 rounded-lg hover:bg-red-100 transition-all"
                          style={{ color: '#d32f2f' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Holidays */}
      <div
        className="p-6 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
      >
        <h3 className="mb-4 flex items-center gap-2" style={{ color: '#444444' }}>
          <Calendar size={20} style={{ color: '#F29B70' }} />
          Holidays (Closed Dates)
        </h3>

        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="date"
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              className="flex-1 px-4 py-2 rounded-lg border"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: '#E0D5D1',
                color: '#444444',
              }}
            />
            <button
              onClick={addHoliday}
              disabled={!newHoliday}
              className="px-4 py-2 rounded-lg hover:opacity-80 transition-all flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#F29B70', color: '#444444' }}
            >
              <Plus size={18} />
              Add Holiday
            </button>
          </div>

          {settings.holidays.length > 0 && (
            <div className="space-y-2">
              {settings.holidays.map((holiday) => (
                <div
                  key={holiday}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(242, 155, 112, 0.1)' }}
                >
                  <span style={{ color: '#444444' }}>
                    {new Date(holiday + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  <button
                    onClick={() => removeHoliday(holiday)}
                    className="p-2 rounded-lg hover:bg-red-100 transition-all"
                    style={{ color: '#d32f2f' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {settings.holidays.length === 0 && (
            <p className="text-center py-4" style={{ color: '#444444' }}>
              No holidays set
            </p>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
          style={{ backgroundColor: '#F29B70', color: '#444444' }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}